<template>
  <ElDialog 
    v-model="visibleProxy" 
    :title="dialogTitle" 
    width="500px" 
    class="add-var-dialog" 
    center
  >
    <div class="dialog-content">
      <div class="form-row">
        <label class="form-label">类型:</label>
        <ElSelect v-model="form.type" class="form-select" placeholder="选择类型">
          <ElOption value="string" label="字符串" />
          <ElOption value="number" label="数字" />
          <ElOption value="boolean" label="布尔" />
          <ElOption value="json" label="JSON" />
          <ElOption value="color" label="颜色" />
          <ElOption value="vec2" label="二维向量" />
          <ElOption value="vec3" label="三维向量" />
        </ElSelect>
      </div>
      
      <div class="form-row">
        <label class="form-label">键名:</label>
        <ElInput v-model="form.key" placeholder="例如：API_BASE_URL" :disabled="mode === 'edit'" />
      </div>
      
      <div class="form-row">
        <label class="form-label">值:</label>
        <ElInput v-if="form.type === 'string'" v-model="form.value" placeholder="例如：https://api.example.com" />
        
        <ElInputNumber
          v-else-if="form.type === 'number'"
          v-model="form.value"
          placeholder="输入数字"
          class="form-input-number"
          :controls="false"
        />
        
        <ElSwitch
          v-else-if="form.type === 'boolean'"
          v-model="form.value"
          class="form-switch"
          active-text="是"
          inactive-text="否"
        />
        
        <div v-else-if="form.type === 'color'" class="color-input-group">
          <ElInput v-model="form.value" placeholder="#FF0000" class="color-text" />
        </div>
        
        <div v-else-if="form.type === 'vec2'" class="vector-input-group">
          <ElInputNumber v-model="form.vec2Value.x" :controls="false" placeholder="X" class="vector-input" />
          <ElInputNumber v-model="form.vec2Value.y" :controls="false" placeholder="Y" class="vector-input" />
        </div>
        
        <div v-else-if="form.type === 'vec3'" class="vector-input-group">
          <ElInputNumber v-model="form.vec3Value.x" :controls="false" placeholder="X" class="vector-input" />
          <ElInputNumber v-model="form.vec3Value.y" :controls="false" placeholder="Y" class="vector-input" />
          <ElInputNumber v-model="form.vec3Value.z" :controls="false" placeholder="Z" class="vector-input" />
        </div>
        
        <ElInput
          v-else-if="form.type === 'json'"
          v-model="form.value"
          type="textarea"
          :rows="4"
          placeholder="输入 JSON 格式的数据"
        />
        
        <ElInput v-else v-model="form.value" placeholder="输入值" />
      </div>
    </div>
    
    <template #footer>
      <span class="dialog-footer">
        <button class="btn btn-secondary" @click="onCancel">取消</button>
        <button class="btn btn-primary" @click="onConfirm">{{ mode === 'add' ? '添加' : '更新' }}</button>
      </span>
    </template>
  </ElDialog>
</template>

<script setup lang="ts">
import { reactive, watch, computed } from 'vue'
import { ElDialog, ElSelect, ElOption, ElInput, ElInputNumber, ElSwitch } from '@/plugins/elementPlus'
import { ElMessage } from '@/plugins/elementPlus'
import type { VariableType } from 'mflows'
// removed: unused mflows variable actions imports

const props = defineProps<{
  modelValue: boolean
  mode: 'add' | 'edit'
  scope: 'global' | 'workflow'
  editingVariable?: { key: string; type: VariableType; value: any } | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'submit', payload: { key: string; type: VariableType; value: any }): void
}>()

const visibleProxy = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
})

const form = reactive<{ type: VariableType | 'string'; key: string; value: any; vec2Value: { x: number; y: number }; vec3Value: { x: number; y: number; z: number } }>({
  type: 'string',
  key: '',
  value: '',
  vec2Value: { x: 0, y: 0 },
  vec3Value: { x: 0, y: 0, z: 0 }
})

watch(() => form.type, (newType) => {
  switch (newType) {
    case 'number':
      form.value = typeof form.value === 'number' ? form.value : null
      break
    case 'boolean':
      form.value = typeof form.value === 'boolean' ? form.value : false
      break
    case 'json':
      if (typeof form.value === 'object' && form.value !== null) {
        try { form.value = JSON.stringify(form.value, null, 2) } catch { form.value = '' }
      } else if (typeof form.value !== 'string') {
        form.value = ''
      }
      break
    case 'color':
      if (typeof form.value !== 'string') form.value = ''
      break
    case 'string':
      if (typeof form.value !== 'string') form.value = ''
      break
    case 'vec2':
    case 'vec3':
      break
  }
})

watch(() => [props.mode, props.editingVariable, props.modelValue] as const, ([mode, variable, visible]) => {
  if (!visible) return
  if (mode === 'edit' && variable) {
    form.type = variable.type
    form.key = variable.key
    if (variable.type === 'vec2') {
      form.vec2Value = { ...variable.value }
    } else if (variable.type === 'vec3') {
      form.vec3Value = { ...variable.value }
    } else if (variable.type === 'number') {
      form.value = typeof variable.value === 'number' ? variable.value : Number(variable.value)
    } else if (variable.type === 'boolean') {
      form.value = !!variable.value
    } else if (variable.type === 'json') {
      form.value = typeof variable.value === 'string' ? variable.value : (() => { try { return JSON.stringify(variable.value, null, 2) } catch { return '' } })()
    } else {
      form.value = variable.value ?? ''
    }
  } else {
    resetForm()
  }
}, { immediate: true })

const dialogTitle = computed(() => {
  const scopeText = props.scope === 'global' ? '全局' : '工作流'
  const actionText = props.mode === 'add' ? '添加' : '编辑'
  return `${actionText}${scopeText}变量`
})

function resetForm() {
  form.type = 'string'
  form.key = ''
  form.value = ''
  form.vec2Value = { x: 0, y: 0 }
  form.vec3Value = { x: 0, y: 0, z: 0 }
}

function onCancel() {
  emit('update:modelValue', false)
  resetForm()
}

function onConfirm() {
  if (!form.key.trim()) {
    ElMessage.warning('请输入变量名')
    return
  }

  let finalValue: any
  switch (form.type) {
    case 'vec2':
      finalValue = form.vec2Value
      break
    case 'vec3':
      finalValue = form.vec3Value
      break
    case 'number':
      if (form.value === null || form.value === '' || isNaN(Number(form.value))) {
        ElMessage.warning('请输入有效的数字')
        return
      }
      finalValue = Number(form.value)
      break
    case 'boolean':
      finalValue = !!form.value
      break
    case 'json':
      try {
        finalValue = JSON.parse(typeof form.value === 'string' ? form.value : JSON.stringify(form.value))
      } catch {
        ElMessage.warning('JSON 格式不正确')
        return
      }
      break
    case 'color':
    case 'string':
    default:
      finalValue = typeof form.value === 'string' ? form.value : (form.value ?? '').toString()
      break
  }

  emit('submit', { key: form.key, type: form.type as VariableType, value: finalValue })
}
</script>

<style scoped lang="scss">
.color-input-group { display: flex; align-items: center; gap: 8px; }
.color-text { flex: 1; }
.vector-input-group { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.vector-input { width: 100%; }
.dialog-footer { display: flex; justify-content: flex-end; gap: 8px; }
</style>